import React, { useState } from "react";
import PropTypes from "prop-types";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import StepContent from "./stepContent";
import { SFGridContainerItem, SFGridItem } from "../grid";
import Pill from "./pill";
import getStyled from "../../hooks/getStyled";

//#region Styled

const { styled } = getStyled();

const StyledStepperWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(2.5, 0),
  height: "100%",
  "& .MuiStepper-root": {
    flexWrap: "wrap",
  },
}));

const StepStyled = styled(Step, {
  shouldForwardProp: (prop) => prop !== "stepIndex" && prop !== "inDrawer",
})(({ theme, stepIndex }) => ({
  paddingLeft: 0,
  paddingRight: 0,
  "& .MuiStepButton-root": {
    paddingTop: 0,
    paddingBottom: 0,
  },
  "& .MuiStepLabel-root": {
    width: "100%",
  },
  "& .MuiStepLabel-iconContainer": {
    width: "100%",
    paddingRight: 0,
    "& .MuiStepLabel-labelContainer": {
      display: "none",
    },
  },
  "& + .MuiStepConnector-root": {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  //if screen is smaller than large show two elements per row
  [theme.breakpoints.down("lg")]: {
    width: "40%",
    marginBottom: theme.spacing(2),
    "& + .MuiStepConnector-root": {
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    //hide connector line every second element
    ...(stepIndex % 2 !== 0 && {
      "& + .MuiStepConnector-root": {
        display: "none",
      },
    }),
  },
}));

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  [`& .${stepConnectorClasses.line}`]: {
    borderTop: "1px dashed",
    borderColor: theme.palette.grey350.main,
    position: "relative",
    "&:after": {
      content: "''",
      width: 0,
      height: 0,
      position: "absolute",
      borderTop: "4px solid transparent",
      borderBottom: "4px solid transparent",
      borderLeft: `6px solid ${theme.palette.grey350.main}`,
      bottom: "50%",
      right: "-1px",
      transform: "translate(0, 50%)",
    },
  },
}));

const StyledStepperContent = styled("div", { shouldForwardProp: false })(
  ({ minHeight }) => ({
    marginTop: "0 !important",
    height: "100%",
    ...(minHeight && { minHeight }),
  })
);

//#endregion

const SFPillStepper = ({
  steps,
  minHeight,
  defaultActiveStep = 0,
  pillColor = "blue200",
  inDrawer = false,
}) => {
  const [activeStep, setActiveStep] = useState(defaultActiveStep);

  const clickStepHandler = async (stepIndex, onStepClick) => {
    if (onStepClick) {
      let res = await onStepClick();
      if (!res) {
        return;
      }
    }
    setActiveStep(stepIndex);
  };

  return (
    <>
      <StyledStepperWrapper>
        <SFGridContainerItem justifyContent="space-around">
          <SFGridItem xs={12}>
            <Stepper
              activeStep={activeStep}
              connector={<ColorlibConnector />}
              nonLinear
            >
              {steps.map((step, index) => {
                return (
                  <StepStyled
                    key={index}
                    disabled={step.disabled}
                    stepIndex={index}
                    inDrawer={inDrawer}
                  >
                    <StepButton
                      color="inherit"
                      onClick={() => clickStepHandler(index, step.onClick)}
                      icon={
                        <Pill
                          active={activeStep === index}
                          color={step.pillColor?.fill ?? pillColor}
                          outlineColor={step.pillColor?.outline ?? pillColor}
                          disabled={step.disabled}
                          inDrawer={inDrawer}
                        >
                          {step.label}
                        </Pill>
                      }
                    />
                  </StepStyled>
                );
              })}
            </Stepper>
          </SFGridItem>
        </SFGridContainerItem>
      </StyledStepperWrapper>
      <StyledStepperContent minHeight={minHeight}>
        {steps.map((step, index) => {
          return (
            <StepContent key={index} active={activeStep === index}>
              {step.content}
            </StepContent>
          );
        })}
      </StyledStepperContent>
    </>
  );
};

SFPillStepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      content: PropTypes.node,
      disabled: PropTypes.bool,
      onClick: PropTypes.func,
      pillColor: PropTypes.shape({
        fill: PropTypes.string,
        outline: PropTypes.string,
      }), // if not provided, pillColor prop will be used for both fill and outline
    })
  ).isRequired,
  defaultActiveStep: PropTypes.number,
  minHeight: PropTypes.number,
  pillColor: PropTypes.string,
};

export default SFPillStepper;
